// ConfirmDialog — drop-in replacement for browser `alert()` /
// `confirm()` so the dashboard can use a styled Modal instead of the
// jarring native popup.
//
// Usage:
//   const { alert, confirm } = useConfirmDialog();
//
//   await alert("上傳失敗：HTTP 500");
//   const ok = await confirm("確定要封存？", { destructive: true });
//   if (!ok) return;
//
// Provider mounts once at the dashboard shell. Both methods return
// Promises so call sites read just like the browser globals.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle, Info, X } from "lucide-react";

export interface ConfirmOptions {
  /** Big title above the message. Defaults to "確認" / "提示" by kind. */
  title?: string;
  /** Confirm-button label. Default 「確定」. */
  okLabel?: string;
  /** Cancel-button label. Default 「取消」. Only shown for `confirm()`. */
  cancelLabel?: string;
  /** Render OK button in danger style (red). Use for revoke / archive. */
  destructive?: boolean;
}

interface ConfirmDialogApi {
  /** Promise-based replacement for `window.alert`. Resolves on close. */
  alert: (message: string, opts?: { title?: string; okLabel?: string }) => Promise<void>;
  /** Promise-based replacement for `window.confirm`. Resolves to true
   *  when user clicks OK, false on Cancel / backdrop / Esc. */
  confirm: (message: string, opts?: ConfirmOptions) => Promise<boolean>;
}

interface DialogState {
  kind: "alert" | "confirm";
  title: string;
  message: string;
  okLabel: string;
  cancelLabel: string;
  destructive: boolean;
  /** Single resolver call settles the awaiting Promise — never call
   *  twice. Wrapped in resolveOnce() to guard. */
  resolve: (value: boolean) => void;
}

const ConfirmDialogContext = createContext<ConfirmDialogApi | null>(null);

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const settledRef = useRef(false);

  const close = useCallback((value: boolean): void => {
    if (settledRef.current) return;
    settledRef.current = true;
    dialog?.resolve(value);
    setDialog(null);
  }, [dialog]);

  // Esc dismisses with `false`; the focus trap keeps Tab from
  // escaping the dialog. Backdrop click also dismisses.
  useEffect(() => {
    if (!dialog) return;
    settledRef.current = false;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close(false);
      } else if (e.key === "Enter") {
        e.preventDefault();
        close(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialog, close]);

  const api: ConfirmDialogApi = {
    alert: (message, opts) => new Promise<void>((resolve) => {
      setDialog({
        kind: "alert",
        title: opts?.title ?? "提示",
        message,
        okLabel: opts?.okLabel ?? "確定",
        cancelLabel: "",
        destructive: false,
        resolve: () => resolve(),
      });
    }),
    confirm: (message, opts) => new Promise<boolean>((resolve) => {
      setDialog({
        kind: "confirm",
        title: opts?.title ?? "確認",
        message,
        okLabel: opts?.okLabel ?? "確定",
        cancelLabel: opts?.cancelLabel ?? "取消",
        destructive: opts?.destructive ?? false,
        resolve,
      });
    }),
  };

  return (
    <ConfirmDialogContext.Provider value={api}>
      {children}
      {dialog && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
          className="fixed inset-0 z-[10000] flex items-center justify-center px-4"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in"
            onClick={() => close(false)}
            aria-hidden
          />
          {/* Card */}
          <div
            className="relative w-full max-w-sm rounded-xl border border-border bg-bg-surface shadow-2xl animate-in fade-in zoom-in-95 duration-150"
          >
            <header className="flex items-start gap-3 px-5 pt-4">
              <span
                className={`mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full ${
                  dialog.destructive
                    ? "bg-danger/15 text-danger"
                    : dialog.kind === "alert"
                      ? "bg-warning/15 text-warning"
                      : "bg-accent/15 text-accent"
                }`}
              >
                {dialog.destructive || dialog.kind === "alert"
                  ? <AlertTriangle className="h-3.5 w-3.5" />
                  : <Info className="h-3.5 w-3.5" />}
              </span>
              <h2 id="confirm-dialog-title" className="flex-1 text-base font-semibold text-text-primary">
                {dialog.title}
              </h2>
              <button
                onClick={() => close(false)}
                className="text-text-muted hover:text-text-primary"
                aria-label="關閉"
              >
                <X className="h-4 w-4" />
              </button>
            </header>
            <div className="px-5 pt-3 pb-5 text-sm text-text-secondary leading-relaxed whitespace-pre-line">
              {dialog.message}
            </div>
            <footer className="flex justify-end gap-2 px-5 pb-5">
              {dialog.kind === "confirm" && (
                <button
                  onClick={() => close(false)}
                  className="btn-secondary text-sm"
                  autoFocus={!dialog.destructive}
                >
                  {dialog.cancelLabel}
                </button>
              )}
              <button
                onClick={() => close(true)}
                className={`text-sm ${dialog.destructive ? "btn-danger" : "btn-primary"}`}
                autoFocus={dialog.destructive || dialog.kind === "alert"}
              >
                {dialog.okLabel}
              </button>
            </footer>
          </div>
        </div>
      )}
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog(): ConfirmDialogApi {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    // Fall back to browser globals so code that runs outside the
    // provider (e.g. a stray test / storybook entry) still works.
    return {
      alert: (m) => { window.alert(m); return Promise.resolve(); },
      confirm: (m) => Promise.resolve(window.confirm(m)),
    };
  }
  return ctx;
}
